'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'hi';

const LANG_KEY = 'dgaj_language';

const translations = {
  en: {
    // Navigation
    myDashboard: 'My Dashboard',
    attendance: 'Attendance',
    applyForLeave: 'Apply for Leave',
    tasks: 'Tasks',
    leaveHolidays: 'Leave & Holidays',
    holidayManagement: 'Holiday Management',
    payroll: 'Payroll',
    myProfile: 'My Profile',
    calendar: 'Calendar',
    ticketCentre: 'Ticket Centre',
    expenseCentre: 'Expense Centre',
    chat: 'Chat',
    documents: 'Documents',
    analytics: 'Analytics',
    people: 'People',
    reports: 'Reports',
    userManagement: 'User Management',
    liveLocations: 'Live Locations',
    attendanceAudit: 'Attendance Audit',
    recurringTasks: 'Recurring Tasks',
    commandHub: 'Command Hub',
    directorPanel: 'Director Panel',
    firmConfiguration: 'Firm Configuration',
    directorSettings: 'Director Settings',
    appSettings: 'App Settings',
    notifications: 'Notifications',
    settings: 'Settings',
    signOut: 'Sign Out',
    installApp: 'Install App',
    attendanceRecords: 'Attendance Records',
    taskAnalytics: 'Task Analytics',
    leaveAdmin: 'Leave Admin',
    leaveDashboard: 'Leave Dashboard',
    clientDiscrepancies: 'Client Discrepancies',
    auditLog: 'Audit Log',
    firmReports: 'Firm Reports',
    directorOnly: 'Director Only',
    tasksGroup: 'Tasks',
    quickAccess: 'Quick Access',
    installDgajConnect: 'Install DGaj Connect',
    signOutWorkHours: 'Sign Out During Work Hours?',
    closeMenu: 'Close menu',
    // Groups
    workspace: 'Workspace',
    collaborate: 'Collaborate',
    resources: 'Resources',
    management: 'Management',
    oversight: 'Oversight',
    // App Settings
    appSettingsTitle: 'App Settings',
    appSettingsDesc: 'Personalise your DGaj Connect experience',
    appearance: 'Appearance',
    appearanceDesc: 'Choose between light mode, dark mode, or follow your system preference.',
    light: 'Light',
    dark: 'Dark',
    system: 'System',
    fontFamily: 'Font Family',
    fontFamilyDesc: 'Select the typeface used throughout the app.',
    fontSize: 'Font Size',
    fontSizeDesc: 'Adjust the base text size for readability.',
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
    interfacePreferences: 'Interface Preferences',
    compactMode: 'Compact Mode',
    compactModeDesc: 'Reduce spacing and padding for a denser layout',
    animations: 'Animations',
    animationsDesc: 'Enable smooth transitions and micro-animations',
    language: 'Language',
    languageDesc: 'Choose the display language for the app.',
    english: 'English',
    hindi: 'हिंदी (Hindi)',
    resetDefaults: 'Reset to defaults',
    preferenceUpdated: 'Preference updated',
    settingsReset: 'Settings reset to defaults',
    // Common
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    loading: 'Loading...',
    noData: 'No data available',
    error: 'Error',
    success: 'Success',
    submit: 'Submit',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    viewAll: 'View all',
    noDataYet: 'No data yet',
    all: 'All',
    refresh: 'Refresh',
    exportReport: 'Export Report',
    // Dashboard
    welcomeBack: 'Welcome back',
    goodMorning: 'Good morning 👋',
    dashboardReady: 'Your dashboard is ready',
    clockIn: 'Clock In',
    clockOut: 'Clock Out',
    present: 'Present',
    absent: 'Absent',
    late: 'Late',
    onLeave: 'On Leave',
    workFromHome: 'Work From Home',
    totalEmployees: 'Total Employees',
    todayAttendance: "Today\'s Attendance",
    pendingLeaves: 'Pending Leaves',
    openTickets: 'Open Tickets',
    myTasks: 'My Tasks',
    noTasksYet: 'No tasks yet',
    tasksWillAppear: 'Tasks assigned to you will appear here',
    noTasksAssigned: 'No tasks assigned yet',
    recentActivity: 'Recent Activity',
    noRecentActivity: 'No recent activity',
    activityWillAppear: 'Your activity will appear here as you use the app',
    leaveBalance: 'Leave Balance',
    currentFinancialYear: 'Current financial year',
    noLeaveData: 'No leave data yet',
    leaveBalanceDesc: 'Leave balances will appear once configured by your admin',
    applyLeave: 'Apply Leave',
    raiseTicket: 'Raise Ticket',
    newTask: 'New Task',
    // KPI
    tasksCompleted: 'Tasks Completed',
    thisWeek: 'This week',
    productivityScore: 'Productivity Score',
    outOf100Today: 'Out of 100 today',
    casualLeave: 'Casual Leave',
    daysRemaining: 'Days remaining',
    overtimeHours: 'Overtime Hours',
    thisMonth: 'This month',
    noOpenTickets: 'No open tickets',
    attendanceRate: 'Attendance Rate',
    currentMonth: 'Current month',
    // Leave
    leaveRequest: 'Leave Request',
    approved: 'Approve',
    rejected: 'Reject',
    pending: 'Pending',
    annualLeave: 'Annual Leave',
    sickLeave: 'Sick Leave',
    // Employee
    employee: 'Employee',
    department: 'Department',
    designation: 'Designation',
    joinDate: 'Join Date',
    email: 'Email',
    phone: 'Phone',
    // Director
    directorControlPanel: 'Director Control Panel',
    teamOverview: 'Team Overview',
    broadcastAnnouncement: 'Broadcast Announcement',
    companyWideAnnouncement: 'Company-Wide Announcement',
    announcementPlaceholder: 'Type your announcement here — it will be broadcast to all employees immediately…',
    sendToAll: 'Send to All',
    liveEmployeeStatus: 'Live Employee Status',
    realTime: 'Real-time',
    quickApprovals: 'Quick Approvals',
    pendingReview: 'pending your review',
    allCaughtUp: 'All caught up!',
    noPendingApprovals: 'No pending approvals',
    redFlagSummary: 'Red Flag Summary',
    redFlagDesc: 'Employees requiring immediate attention',
    fullReport: 'Full Report',
    attendanceIssues: 'Attendance Issues',
    lowProductivity: 'Low Productivity',
    overdueTasks: 'Overdue Tasks',
    turnoverRisk: 'Turnover Risk',
    noIssuesFlagged: 'No issues flagged',
    noEmployeesToDisplay: 'No employees to display',
    employeeDataWillAppear: 'Employee data will appear here once added',
    completeCompanyOversight: 'Complete company oversight',
    // Task Board
    taskBoard: 'Task Board',
    addTask: 'Add Task',
    allTasks: 'All Tasks',
    todo: 'To Do',
    inProgress: 'In Progress',
    active: 'Active',
    review: 'Review',
    done: 'Done',
    overdue: 'Overdue',
    priority: 'Priority',
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    dueDate: 'Due Date',
    assignedTo: 'Assigned To',
    showingTasks: 'Showing',
    ofTasks: 'tasks',
    // User Management
    addUser: 'Add User',
    users: 'Users',
    requests: 'Requests',
    pins: 'PINs',
    role: 'Role',
    status: 'Status',
    inactive: 'Inactive',
    // Leave Management
    applyForLeaveTitle: 'Apply for Leave',
    leaveHistory: 'Leave History',
    leaveType: 'Leave Type',
    startDate: 'Start Date',
    endDate: 'End Date',
    reason: 'Reason',
    // Notifications
    notificationsTitle: 'Notifications',
    markAllRead: 'Mark all read',
    allNotifications: 'All',
    unread: 'Unread',
    noUnreadNotifications: 'No unread notifications',
    noNotificationsYet: 'No notifications yet',
    allAppAlerts: 'All app alerts — leave, tasks, tickets, expenses & more',
    enablePushNotifications: 'Enable Mobile Push Notifications',
    pushNotificationsDesc: 'Get notified on your device even when the app is in the background',
    enable: 'Enable',
    pushNotificationsActive: 'Push notifications are active on this device',
    // Analytics
    analyticsTitle: 'Analytics & Reporting',
    companyWideVisibility: 'Company-wide workforce visibility',
    lastUpdated: 'Last updated',
    totalHeadcount: 'Total Headcount',
    avgAttendanceRate: 'Avg Attendance Rate',
    last30Days: 'Last 30 days',
    avgProductivityScore: 'Avg Productivity Score',
    companyWide: 'Company-wide',
    pendingApprovals: 'Pending Approvals',
    noPendingApprovalsShort: 'No pending approvals',
    overTimeCost: 'Overtime Cost',
    taskCompletionRate: 'Task Completion Rate',
    awaitingData: 'Awaiting data',
    employeesFlagged: 'Employees flagged',
    noDataShort: 'No data yet',
    // Red Flag
    redFlagAlerts: 'Red Flag Alerts',
    activeNeedsHR: 'active · Needs HR review',
    noActiveFlags: 'No active flags',
    allDismissedOrFiltered: 'All dismissed or filtered out',
    flaggedYesterday: 'Flagged yesterday',
    flaggedDaysAgo: 'days ago',
    review: 'Review',
    dismiss: 'Dismiss',
    viewAllRiskSignals: 'View all employee risk signals →',
    // Ticket Centre
    ticketCentreTitle: 'Ticket Centre',
    newTicket: 'New Ticket',
    // Expense Centre
    expenseCentreTitle: 'Expense Centre',
    newExpense: 'New Expense',
    // Profile
    employeeProfile: 'Employee Profile',
    editProfile: 'Edit Profile',
    // Calendar
    companyCalendar: 'Company Calendar',
    // Attendance Audit
    attendanceAuditTitle: 'Attendance Audit',
    // Recurring Tasks
    recurringTasksTitle: 'Recurring Tasks',
    // Command Hub
    commandHubTitle: 'Command Hub',
    // Director Settings
    directorSettingsTitle: 'Director Settings',
    // Firm Configuration
    firmConfigTitle: 'Firm Configuration',
    // Live Location
    liveLocationTitle: 'Live Location Map',
    // Holiday Management
    holidayManagementTitle: 'Holiday Management',
    addHoliday: 'Add Holiday',
    // Attendance
    clockedIn: 'Clocked In',
    clockedOut: 'Clocked Out',
    notClockedIn: 'Not Clocked In',
    locationGranted: 'Location granted',
    locationDenied: 'Location denied',
    locationUnavailable: 'Location unavailable',
    travelApproved: 'Travel approved — no restrictions',
  },
  hi: {
    // Navigation
    myDashboard: 'मेरा डैशबोर्ड',
    attendance: 'उपस्थिति',
    applyForLeave: 'छुट्टी के लिए आवेदन',
    tasks: 'कार्य',
    leaveHolidays: 'छुट्टी और अवकाश',
    holidayManagement: 'अवकाश प्रबंधन',
    payroll: 'वेतन',
    myProfile: 'मेरी प्रोफ़ाइल',
    calendar: 'कैलेंडर',
    ticketCentre: 'टिकट केंद्र',
    expenseCentre: 'व्यय केंद्र',
    chat: 'चैट',
    documents: 'दस्तावेज़',
    analytics: 'विश्लेषण',
    people: 'लोग',
    reports: 'रिपोर्ट',
    userManagement: 'उपयोगकर्ता प्रबंधन',
    liveLocations: 'लाइव स्थान',
    attendanceAudit: 'उपस्थिति ऑडिट',
    recurringTasks: 'आवर्ती कार्य',
    commandHub: 'कमांड हब',
    directorPanel: 'निदेशक पैनल',
    firmConfiguration: 'फर्म कॉन्फ़िगरेशन',
    directorSettings: 'निदेशक सेटिंग्स',
    appSettings: 'ऐप सेटिंग्स',
    notifications: 'सूचनाएं',
    settings: 'सेटिंग्स',
    signOut: 'साइन आउट',
    installApp: 'ऐप इंस्टॉल करें',
    attendanceRecords: 'उपस्थिति रिकॉर्ड',
    taskAnalytics: 'कार्य विश्लेषण',
    leaveAdmin: 'छुट्टी एडमिन',
    leaveDashboard: 'छुट्टी डैशबोर्ड',
    clientDiscrepancies: 'क्लाइंट विसंगतियां',
    auditLog: 'ऑडिट लॉग',
    firmReports: 'फर्म रिपोर्ट',
    directorOnly: 'केवल निदेशक',
    tasksGroup: 'कार्य',
    quickAccess: 'त्वरित पहुंच',
    installDgajConnect: 'DGaj Connect इंस्टॉल करें',
    signOutWorkHours: 'कार्य समय के दौरान साइन आउट करें?',
    closeMenu: 'मेनू बंद करें',
    // Groups
    workspace: 'कार्यक्षेत्र',
    collaborate: 'सहयोग',
    resources: 'संसाधन',
    management: 'प्रबंधन',
    oversight: 'निगरानी',
    // App Settings
    appSettingsTitle: 'ऐप सेटिंग्स',
    appSettingsDesc: 'अपना DGaj Connect अनुभव व्यक्तिगत बनाएं',
    appearance: 'दिखावट',
    appearanceDesc: 'लाइट मोड, डार्क मोड, या सिस्टम प्राथमिकता चुनें।',
    light: 'लाइट',
    dark: 'डार्क',
    system: 'सिस्टम',
    fontFamily: 'फ़ॉन्ट परिवार',
    fontFamilyDesc: 'ऐप में उपयोग किया जाने वाला टाइपफेस चुनें।',
    fontSize: 'फ़ॉन्ट आकार',
    fontSizeDesc: 'पठनीयता के लिए बेस टेक्स्ट आकार समायोजित करें।',
    small: 'छोटा',
    medium: 'मध्यम',
    large: 'बड़ा',
    interfacePreferences: 'इंटरफ़ेस प्राथमिकताएं',
    compactMode: 'कॉम्पैक्ट मोड',
    compactModeDesc: 'घने लेआउट के लिए स्पेसिंग और पैडिंग कम करें',
    animations: 'एनिमेशन',
    animationsDesc: 'स्मूथ ट्रांजिशन और माइक्रो-एनिमेशन सक्षम करें',
    language: 'भाषा',
    languageDesc: 'ऐप के लिए प्रदर्शन भाषा चुनें।',
    english: 'English',
    hindi: 'हिंदी (Hindi)',
    resetDefaults: 'डिफ़ॉल्ट पर रीसेट करें',
    preferenceUpdated: 'प्राथमिकता अपडेट की गई',
    settingsReset: 'सेटिंग्स डिफ़ॉल्ट पर रीसेट की गईं',
    // Common
    save: 'सहेजें',
    cancel: 'रद्द करें',
    confirm: 'पुष्टि करें',
    delete: 'हटाएं',
    edit: 'संपादित करें',
    add: 'जोड़ें',
    search: 'खोजें',
    filter: 'फ़िल्टर',
    loading: 'लोड हो रहा है...',
    noData: 'कोई डेटा उपलब्ध नहीं',
    error: 'त्रुटि',
    success: 'सफलता',
    submit: 'जमा करें',
    close: 'बंद करें',
    back: 'वापस',
    next: 'अगला',
    previous: 'पिछला',
    viewAll: 'सभी देखें',
    noDataYet: 'अभी कोई डेटा नहीं',
    all: 'सभी',
    refresh: 'रीफ्रेश',
    exportReport: 'रिपोर्ट निर्यात करें',
    // Dashboard
    welcomeBack: 'वापस स्वागत है',
    goodMorning: 'शुभ प्रभात 👋',
    dashboardReady: 'आपका डैशबोर्ड तैयार है',
    clockIn: 'क्लॉक इन',
    clockOut: 'क्लॉक आउट',
    present: 'उपस्थित',
    absent: 'अनुपस्थित',
    late: 'देर से',
    onLeave: 'छुट्टी पर',
    workFromHome: 'घर से काम',
    totalEmployees: 'कुल कर्मचारी',
    todayAttendance: 'आज की उपस्थिति',
    pendingLeaves: 'लंबित छुट्टियां',
    openTickets: 'खुले टिकट',
    myTasks: 'मेरे कार्य',
    noTasksYet: 'अभी कोई कार्य नहीं',
    tasksWillAppear: 'आपको सौंपे गए कार्य यहाँ दिखेंगे',
    noTasksAssigned: 'अभी कोई कार्य नहीं सौंपा गया',
    recentActivity: 'हाल की गतिविधि',
    noRecentActivity: 'कोई हाल की गतिविधि नहीं',
    activityWillAppear: 'जैसे-जैसे आप ऐप उपयोग करेंगे, आपकी गतिविधि यहाँ दिखेगी',
    leaveBalance: 'छुट्टी शेष',
    currentFinancialYear: 'वर्तमान वित्तीय वर्ष',
    noLeaveData: 'अभी कोई छुट्टी डेटा नहीं',
    leaveBalanceDesc: 'आपके एडमिन द्वारा कॉन्फ़िगर होने पर छुट्टी शेष दिखेगा',
    applyLeave: 'छुट्टी आवेदन',
    raiseTicket: 'टिकट उठाएं',
    newTask: 'नया कार्य',
    // KPI
    tasksCompleted: 'पूर्ण कार्य',
    thisWeek: 'इस सप्ताह',
    productivityScore: 'उत्पादकता स्कोर',
    outOf100Today: 'आज 100 में से',
    casualLeave: 'आकस्मिक छुट्टी',
    daysRemaining: 'दिन शेष',
    overtimeHours: 'ओवरटाइम घंटे',
    thisMonth: 'इस महीने',
    noOpenTickets: 'कोई खुला टिकट नहीं',
    attendanceRate: 'उपस्थिति दर',
    currentMonth: 'वर्तमान महीना',
    // Leave
    leaveRequest: 'छुट्टी अनुरोध',
    approved: 'स्वीकृत करें',
    rejected: 'अस्वीकृत करें',
    pending: 'लंबित',
    annualLeave: 'वार्षिक छुट्टी',
    sickLeave: 'बीमारी की छुट्टी',
    // Employee
    employee: 'कर्मचारी',
    department: 'विभाग',
    designation: 'पदनाम',
    joinDate: 'शामिल होने की तारीख',
    email: 'ईमेल',
    phone: 'फ़ोन',
    // Director
    directorControlPanel: 'निदेशक नियंत्रण पैनल',
    teamOverview: 'टीम अवलोकन',
    broadcastAnnouncement: 'घोषणा प्रसारित करें',
    companyWideAnnouncement: 'कंपनी-व्यापी घोषणा',
    announcementPlaceholder: 'यहाँ अपनी घोषणा टाइप करें — यह तुरंत सभी कर्मचारियों को प्रसारित होगी…',
    sendToAll: 'सभी को भेजें',
    liveEmployeeStatus: 'लाइव कर्मचारी स्थिति',
    realTime: 'रियल-टाइम',
    quickApprovals: 'त्वरित अनुमोदन',
    pendingReview: 'समीक्षा के लिए लंबित',
    allCaughtUp: 'सब पूर्ण!',
    noPendingApprovals: 'कोई लंबित अनुमोदन नहीं',
    redFlagSummary: 'रेड फ्लैग सारांश',
    redFlagDesc: 'तत्काल ध्यान की आवश्यकता वाले कर्मचारी',
    fullReport: 'पूरी रिपोर्ट',
    attendanceIssues: 'उपस्थिति समस्याएं',
    lowProductivity: 'कम उत्पादकता',
    overdueTasks: 'अतिदेय कार्य',
    turnoverRisk: 'टर्नओवर जोखिम',
    noIssuesFlagged: 'कोई समस्या नहीं',
    noEmployeesToDisplay: 'दिखाने के लिए कोई कर्मचारी नहीं',
    employeeDataWillAppear: 'जोड़े जाने पर कर्मचारी डेटा यहाँ दिखेगा',
    completeCompanyOversight: 'पूर्ण कंपनी निगरानी',
    // Task Board
    taskBoard: 'कार्य बोर्ड',
    addTask: 'कार्य जोड़ें',
    allTasks: 'सभी कार्य',
    todo: 'करना है',
    inProgress: 'प्रगति में',
    active: 'सक्रिय',
    review: 'समीक्षा',
    done: 'पूर्ण',
    overdue: 'अतिदेय',
    priority: 'प्राथमिकता',
    critical: 'अत्यावश्यक',
    high: 'उच्च',
    medium: 'मध्यम',
    low: 'निम्न',
    dueDate: 'नियत तारीख',
    assignedTo: 'सौंपा गया',
    showingTasks: 'दिखा रहे हैं',
    ofTasks: 'कार्य',
    // User Management
    addUser: 'उपयोगकर्ता जोड़ें',
    users: 'उपयोगकर्ता',
    requests: 'अनुरोध',
    pins: 'पिन',
    role: 'भूमिका',
    status: 'स्थिति',
    inactive: 'निष्क्रिय',
    // Leave Management
    applyForLeaveTitle: 'छुट्टी के लिए आवेदन',
    leaveHistory: 'छुट्टी इतिहास',
    leaveType: 'छुट्टी का प्रकार',
    startDate: 'शुरू की तारीख',
    endDate: 'समाप्ति तारीख',
    reason: 'कारण',
    // Notifications
    notificationsTitle: 'सूचनाएं',
    markAllRead: 'सभी पढ़े हुए चिह्नित करें',
    allNotifications: 'सभी',
    unread: 'अपठित',
    noUnreadNotifications: 'कोई अपठित सूचना नहीं',
    noNotificationsYet: 'अभी कोई सूचना नहीं',
    allAppAlerts: 'सभी ऐप अलर्ट — छुट्टी, कार्य, टिकट, व्यय और अधिक',
    enablePushNotifications: 'मोबाइल पुश सूचनाएं सक्षम करें',
    pushNotificationsDesc: 'जब ऐप बैकग्राउंड में हो तब भी अपने डिवाइस पर सूचना पाएं',
    enable: 'सक्षम करें',
    pushNotificationsActive: 'इस डिवाइस पर पुश सूचनाएं सक्रिय हैं',
    // Analytics
    analyticsTitle: 'विश्लेषण और रिपोर्टिंग',
    companyWideVisibility: 'कंपनी-व्यापी कार्यबल दृश्यता',
    lastUpdated: 'अंतिम अपडेट',
    totalHeadcount: 'कुल कर्मचारी संख्या',
    avgAttendanceRate: 'औसत उपस्थिति दर',
    last30Days: 'पिछले 30 दिन',
    avgProductivityScore: 'औसत उत्पादकता स्कोर',
    companyWide: 'कंपनी-व्यापी',
    pendingApprovals: 'लंबित अनुमोदन',
    noPendingApprovalsShort: 'कोई लंबित अनुमोदन नहीं',
    overTimeCost: 'ओवरटाइम लागत',
    taskCompletionRate: 'कार्य पूर्णता दर',
    awaitingData: 'डेटा की प्रतीक्षा',
    employeesFlagged: 'चिह्नित कर्मचारी',
    noDataShort: 'अभी कोई डेटा नहीं',
    // Red Flag
    redFlagAlerts: 'रेड फ्लैग अलर्ट',
    activeNeedsHR: 'सक्रिय · HR समीक्षा आवश्यक',
    noActiveFlags: 'कोई सक्रिय फ्लैग नहीं',
    allDismissedOrFiltered: 'सभी खारिज या फ़िल्टर किए गए',
    flaggedYesterday: 'कल चिह्नित किया गया',
    flaggedDaysAgo: 'दिन पहले चिह्नित',
    dismiss: 'खारिज करें',
    viewAllRiskSignals: 'सभी कर्मचारी जोखिम संकेत देखें →',
    // Ticket Centre
    ticketCentreTitle: 'टिकट केंद्र',
    newTicket: 'नया टिकट',
    // Expense Centre
    expenseCentreTitle: 'व्यय केंद्र',
    newExpense: 'नया व्यय',
    // Profile
    employeeProfile: 'कर्मचारी प्रोफ़ाइल',
    editProfile: 'प्रोफ़ाइल संपादित करें',
    // Calendar
    companyCalendar: 'कंपनी कैलेंडर',
    // Attendance Audit
    attendanceAuditTitle: 'उपस्थिति ऑडिट',
    // Recurring Tasks
    recurringTasksTitle: 'आवर्ती कार्य',
    // Command Hub
    commandHubTitle: 'कमांड हब',
    // Director Settings
    directorSettingsTitle: 'निदेशक सेटिंग्स',
    // Firm Configuration
    firmConfigTitle: 'फर्म कॉन्फ़िगरेशन',
    // Live Location
    liveLocationTitle: 'लाइव लोकेशन मैप',
    // Holiday Management
    holidayManagementTitle: 'अवकाश प्रबंधन',
    addHoliday: 'अवकाश जोड़ें',
    // Attendance
    clockedIn: 'क्लॉक इन हो गए',
    clockedOut: 'क्लॉक आउट हो गए',
    notClockedIn: 'क्लॉक इन नहीं किया',
    locationGranted: 'स्थान अनुमति मिली',
    locationDenied: 'स्थान अनुमति अस्वीकृत',
    locationUnavailable: 'स्थान अनुपलब्ध',
    travelApproved: 'यात्रा स्वीकृत — कोई प्रतिबंध नहीं',
  },
};

export type TranslationKey = keyof typeof translations.en;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key) => translations.en[key] ?? key,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>('en');

  useEffect(() => {
    try {
      const stored = localStorage.getItem(LANG_KEY) as Language | null;
      if (stored === 'en' || stored === 'hi') {
        setLanguageState(stored);
      }
    } catch {}
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(LANG_KEY, lang);
    } catch {}
  };

  const t = (key: TranslationKey): string => {
    return (translations[language] as Record<string, string>)[key] ?? (translations.en as Record<string, string>)[key] ?? key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}